import { GraphQLResult } from "@aws-amplify/api-graphql";
import {
  type UseQueryResult,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  AppointmentRecord,
  AppointmentRecordsByOriginalAppointmentIDQuery,
  AppointmentStatus,
  AppointmentType,
  BillingStatus,
  NoShowReason,
  ParticipantType,
  ReportStatus,
} from "~/api/API";
import { appointmentRecordsByOriginalAppointmentIDOptimized } from "~/api/graphql-custom/queries";
import {
  appointmentFilesQueryConfig,
  FileDisplayData,
  sourceAppointmentFilesQueryConfig,
} from "~/api/queries/useAppointmentFiles";
import { useCurrentUser } from "~/auth/UserContext";
import { amplifyGraphQLClient } from "~/lib/amplify-client";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { useFeatureFlag } from "~/services/featureFlags/provider";

/** Appointment info needed to fetch files and provide metadata */
export interface LinkedAppointmentInfo {
  id: string;
  s3FileKey?: string | null;
  date?: string | null;
  type?: AppointmentType | null;
  status?: AppointmentStatus | null;
  billingStatus?: BillingStatus | null;
  reportStatus?: ReportStatus | null;
  rescheduledFromAppointmentID?: string | null;
  rescheduledToAppointmentID?: string | null;
  reschedulingRequestedBy?: ParticipantType | null;
  reschedulingRequestedInternally?: boolean | null;
  noShowReason?: NoShowReason | null;
  isDemo?: boolean | null;
}

export const linkedAppointmentKeys = {
  all: ["linkedAppointmentsByOriginal"] as const,
  byOriginal: (originalAppointmentID: string, isDemo?: boolean) =>
    [...linkedAppointmentKeys.all, originalAppointmentID, isDemo] as const,
};

// ============================================================================
// Step 1: Fetch all linked appointments via the originalAppointmentID GSI
// ============================================================================

/**
 * Hook that fetches all appointments linked via the originalAppointmentID GSI.
 *
 * Every appointment in a chain (original, supplementals, depositions) has
 * originalAppointmentID set to the original appointment's ID (the original
 * points to itself). Querying the GSI returns ALL appointments in the chain.
 */
export function useLinkedAppointments(
  originalID: string,
  options?: { enabled?: boolean },
) {
  const { isDemo } = useCurrentUser();
  const demoFilter = isDemo
    ? { isDemo: { eq: true } }
    : { isDemo: { ne: true } };

  return useQuery({
    queryKey: linkedAppointmentKeys.byOriginal(originalID, isDemo),
    queryFn: async (): Promise<LinkedAppointmentInfo[]> => {
      const result =
        (await amplifyGraphQLClient.graphql<AppointmentRecordsByOriginalAppointmentIDQuery>(
          {
            query: appointmentRecordsByOriginalAppointmentIDOptimized,
            variables: {
              originalAppointmentID: originalID,
              filter: demoFilter,
            },
          },
        )) as GraphQLResult<AppointmentRecordsByOriginalAppointmentIDQuery>;

      const response = result.data?.appointmentRecordsByOriginalAppointmentID;

      if (response?.nextToken) {
        console.warn(
          `[useLinkedAppointments] Paginated response for originalAppointmentID=${originalID} — some linked appointments may be missing`,
        );
      }

      const items = response?.items ?? [];

      return items
        .filter((item): item is NonNullable<typeof item> => item !== null)
        .map((item) => ({
          id: item.id,
          s3FileKey: item.s3FileKey,
          date: item.date,
          type: item.type,
          status: item.status,
          billingStatus: item.billingStatus,
          reportStatus: item.reportStatus,
          rescheduledFromAppointmentID: item.rescheduledFromAppointmentID,
          rescheduledToAppointmentID: item.rescheduledToAppointmentID,
          reschedulingRequestedBy: item.reschedulingRequestedBy,
          reschedulingRequestedInternally: item.reschedulingRequestedInternally,
          noShowReason: item.noShowReason,
          isDemo: item.isDemo,
        }));
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Step 2: Fetch files for multiple appointments in parallel
// ============================================================================

interface UseAllAppointmentFilesOptions {
  /** All appointments to fetch files for */
  appointments: LinkedAppointmentInfo[];
  /** Whether the appointments are still loading (disables file queries) */
  isLoadingAppointments: boolean;
}

/**
 * Hook that fetches files for a list of appointments in parallel using useQueries,
 * returning a map of appointment ID → files.
 *
 * Read path is flag-selected per appointment:
 * - PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT on → sourceAppointmentFilesQueryConfig
 *   (UploadedFile.bySourceAppointment GSI; every file ORIGINAL at its source,
 *   inheritance derived downstream by resolveFileOwnership).
 * - off (or still loading) → appointmentFilesQueryConfig (legacy junction read,
 *   sharing cache with useAppointmentFiles).
 *
 * The two configs use distinct cache keys, so flipping the flag re-fetches under
 * the new key rather than reading stale data of the other shape.
 */
export function useAllAppointmentFiles({
  appointments,
  isLoadingAppointments,
}: UseAllAppointmentFilesOptions) {
  // Default to the legacy junction read while the flag resolves, so file lists
  // never regress to an empty/partial source read before the flag is known.
  const { isEnabled: readBySourceAppointment } = useFeatureFlag(
    FEATURE_FLAGS.PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT,
  );
  const fileQueryConfig = readBySourceAppointment
    ? sourceAppointmentFilesQueryConfig
    : appointmentFilesQueryConfig;

  // Stable combine: an inline combine is a new reference every render, which
  // makes useQueries re-run it and return a fresh `filesByAppointment` Map each
  // time (replaceEqualDeep can't preserve a Map's identity). That churn
  // cascades downstream (e.g. file-preload effects re-fire every render). A
  // useCallback lets TanStack memoize the combined result across renders where
  // the underlying query results haven't changed.
  const combine = useCallback(
    (results: UseQueryResult<FileDisplayData[], Error>[]) => {
      const filesByAppointment = new Map<string, FileDisplayData[]>();
      for (let i = 0; i < appointments.length; i++) {
        filesByAppointment.set(appointments[i].id, results[i]?.data ?? []);
      }

      return {
        filesByAppointment,
        isLoading: results.some((q) => q.isLoading),
        error: results.find((q) => q.error)?.error ?? null,
      };
    },
    [appointments],
  );

  const { filesByAppointment, isLoading, error } = useQueries({
    queries: appointments.map((apt) => ({
      ...fileQueryConfig(apt.id),
      enabled: !isLoadingAppointments && appointments.length > 0,
    })),
    combine,
  });

  return { filesByAppointment, isLoading, error };
}

// ============================================================================
// Composition Hook: Puts Steps 1 & 2 together
// ============================================================================

interface UseLinkedAppointmentFilesResult {
  /** Map of appointment ID → files visible in that appointment (with isReadOnly per file) */
  filesByAppointment: Map<string, FileDisplayData[]>;
  /** Map of appointment ID → metadata for all appointments in the chain */
  appointmentMap: Map<string, LinkedAppointmentInfo>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook that fetches files for all appointments linked via the originalAppointmentID index.
 *
 * Composes useLinkedAppointments (Step 1) and useAllAppointmentFiles (Step 2):
 * 1. Resolves the originalAppointmentID and queries the GSI to discover all linked appointments
 * 2. Fetches files for each appointment in parallel, returning a map of appointment ID → files
 *
 * Each appointment's files retain their original isReadOnly status from the query.
 * Deduplication and ownership attribution are left to the consumer.
 */
export function useLinkedAppointmentFiles(
  appointment: AppointmentRecord | null | undefined,
): UseLinkedAppointmentFilesResult {
  // Fallback to appointment.id when originalAppointmentID is not set (e.g. data
  // integrity gap). The GSI query will return only this appointment in that case.
  const originalId = appointment?.originalAppointmentID ?? appointment?.id;

  // Step 1: Fetch all appointments in the chain from the originalAppointmentID GSI
  const { data: linkedAppointments = [], isLoading: isLoadingLinked } =
    useLinkedAppointments(originalId ?? "", { enabled: !!originalId });

  // Build complete list of appointments to fetch files for.
  // The GSI returns all appointments in the chain (including the original),
  // but we also include the current appointment to ensure its full data
  // s3FileKey is used if it appears in both.
  const { allAppointments, appointmentMap } = useMemo(() => {
    const map = new Map<string, LinkedAppointmentInfo>();

    // Add current appointment first (has full data from props)
    if (appointment) {
      map.set(appointment.id, {
        id: appointment.id,
        s3FileKey: appointment.s3FileKey,
        date: appointment.date,
        type: appointment.type,
        status: appointment.status,
        billingStatus: appointment.billingStatus,
        reportStatus: appointment.reportStatus,
        rescheduledFromAppointmentID: appointment.rescheduledFromAppointmentID,
        rescheduledToAppointmentID: appointment.rescheduledToAppointmentID,
        reschedulingRequestedBy: appointment.reschedulingRequestedBy,
        reschedulingRequestedInternally:
          appointment.reschedulingRequestedInternally,
        noShowReason: appointment.noShowReason,
        isDemo: appointment.isDemo,
      });
    }

    // Add remaining appointments from GSI query
    for (const linked of linkedAppointments) {
      if (!map.has(linked.id)) {
        map.set(linked.id, linked);
      }
    }

    return {
      allAppointments: Array.from(map.values()),
      appointmentMap: map,
    };
  }, [appointment, linkedAppointments]);

  // Step 2: Fetch files for each appointment
  const {
    filesByAppointment,
    isLoading: isLoadingFiles,
    error,
  } = useAllAppointmentFiles({
    appointments: allAppointments,
    isLoadingAppointments: isLoadingLinked,
  });

  const isLoading = isLoadingLinked || isLoadingFiles;

  return { filesByAppointment, appointmentMap, isLoading, error };
}
